import React, { useState, useEffect } from "react";
import { Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

// ─── Power BI palette ─────────────────────────────────────────────────────────
const PBI = {
  bg: "#f3f2f1",
  surface: "#ffffff",
  headerBg: "#252423",
  headerText: "#ffffff",
  accent: "#e8563a",
  blue: "#118dff",
  blueSoft: "#e8f4ff",
  green: "#12b76a",
  greenSoft: "#e6f9f0",
  amber: "#f79009",
  amberSoft: "#fff8ec",
  red: "#d92d20",
  redSoft: "#fef3f2",
  purple: "#7b5ea7",
  purpleSoft: "#f2eef9",
  greyLight: "#edebe9",
  border: "#d2d0ce",
  textPrimary: "#252423",
  textSecondary: "#605e5c",
  textMuted: "#a19f9d",
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, color, softColor, icon, sub }) => (
  <div style={{
    background: PBI.surface, border: `1px solid ${PBI.border}`,
    borderTop: `3px solid ${color}`, borderRadius: "4px",
    padding: "18px 20px", minWidth: "140px", flex: "1",
  }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
      <span style={{ fontSize: "11px", fontWeight: "600", color: PBI.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {label}
      </span>
      <div style={{ width: "28px", height: "28px", borderRadius: "4px", background: softColor, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <i className={icon} style={{ fontSize: "12px", color }} />
      </div>
    </div>
    <div style={{ fontSize: "28px", fontWeight: "700", color: PBI.textPrimary, lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: "11px", color: PBI.textMuted, marginTop: "4px" }}>{sub}</div>}
  </div>
);

// ─── Status chip ──────────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const map = {
    delivered:    { bg: PBI.greenSoft,  color: PBI.green,  label: "Delivered" },
    failed:       { bg: PBI.redSoft,    color: PBI.red,    label: "Failed" },
    undelivered:  { bg: PBI.amberSoft,  color: PBI.amber,  label: "Undelivered" },
    queued:       { bg: PBI.blueSoft,   color: PBI.blue,   label: "Queued" },
    sent:         { bg: PBI.blueSoft,   color: PBI.blue,   label: "Sent" },
  };
  const s = map[status?.toLowerCase()] || { bg: PBI.greyLight, color: PBI.textMuted, label: status || "—" };
  return (
    <span style={{
      background: s.bg, color: s.color, border: `1px solid ${s.color}33`,
      borderRadius: "3px", padding: "2px 8px", fontSize: "11px", fontWeight: "700",
      letterSpacing: "0.3px", textTransform: "uppercase", whiteSpace: "nowrap",
    }}>
      {s.label}
    </span>
  );
};

// ─── Error chip ───────────────────────────────────────────────────────────────
const ErrorChip = ({ code }) => {
  const isTemp = code && String(code).startsWith("3000");
  return (
    <span style={{
      background: isTemp ? PBI.amberSoft : PBI.redSoft,
      color: isTemp ? PBI.amber : PBI.red,
      border: `1px solid ${isTemp ? "#fddfa2" : "#fda29b"}`,
      borderRadius: "3px", padding: "2px 8px", fontSize: "11px", fontWeight: "700",
    }}>
      {code || "—"}
    </span>
  );
};

// ─── Section card ─────────────────────────────────────────────────────────────
const SectionCard = ({ title, subtitle, children }) => (
  <div style={{ background: PBI.surface, border: `1px solid ${PBI.border}`, borderRadius: "4px", overflow: "hidden", marginBottom: "16px" }}>
    <div style={{ padding: "14px 20px", borderBottom: `1px solid ${PBI.greyLight}` }}>
      <div style={{ fontSize: "13px", fontWeight: "700", color: PBI.textPrimary }}>{title}</div>
      {subtitle && <div style={{ fontSize: "11px", color: PBI.textMuted, marginTop: "2px" }}>{subtitle}</div>}
    </div>
    <div style={{ padding: "16px 20px" }}>{children}</div>
  </div>
);

const thStyle = {
  background: "#2d2c2b", color: "#fff",
  fontSize: "10px", fontWeight: "700", textTransform: "uppercase",
  letterSpacing: "0.5px", padding: "10px 14px", whiteSpace: "nowrap",
  borderBottom: "2px solid #3b3a39", borderRight: "1px solid #3b3a39",
};
const tdStyle = {
  verticalAlign: "middle", padding: "10px 14px",
  fontSize: "13px", color: PBI.textPrimary,
  borderBottom: `1px solid ${PBI.greyLight}`,
};

// ─── Progress bar ─────────────────────────────────────────────────────────────
const ProgressBar = ({ value, color }) => (
  <div style={{ background: PBI.greyLight, borderRadius: "2px", height: "6px", width: "100%", minWidth: "60px" }}>
    <div style={{ width: `${Math.min(value, 100)}%`, height: "100%", background: color, borderRadius: "2px", transition: "width 0.4s" }} />
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const SmsStatistics = () => {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });

  const fetchStatistics = async (startDate = "", endDate = "") => {
    setLoading(true);
    try {
      let url = `${process.env.REACT_APP_DASHBOARD_PROD_URL}/webhooks/twilio/sms-stats`;
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (params.toString()) url += `?${params.toString()}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setStats(data);
      } else {
        toast.error("Failed to fetch SMS statistics");
      }
    } catch (error) {
      console.error("Error fetching SMS statistics:", error);
      toast.error("Error loading statistics");
    } finally {
      setLoading(false);
    }
  };

  const fetchHealth = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_DASHBOARD_PROD_URL}/webhooks/twilio/health`);
      const data = await response.json();
      setHealth(data);
    } catch (error) {
      console.error("Error fetching health:", error);
    }
  };

  useEffect(() => {
    fetchStatistics();
    fetchHealth();
    const interval = setInterval(() => {
      fetchStatistics(dateRange.startDate, dateRange.endDate);
      fetchHealth();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleDateFilter = (e) => {
    e.preventDefault();
    fetchStatistics(dateRange.startDate, dateRange.endDate);
  };

  const handleRefresh = () => {
    fetchStatistics(dateRange.startDate, dateRange.endDate);
    fetchHealth();
    toast.info("Statistics refreshed");
  };

  const handleClearFilter = () => {
    setDateRange({ startDate: "", endDate: "" });
    fetchStatistics();
  };

  const pieChartData = stats
    ? {
        labels: ["Delivered", "Failed", "Undelivered", "Pending"],
        datasets: [{
          data: [stats.summary.delivered, stats.summary.failed, stats.summary.undelivered, stats.summary.pending],
          backgroundColor: [PBI.green, PBI.red, PBI.amber, PBI.blue],
          borderWidth: 2,
          borderColor: "#fff",
        }],
      }
    : null;

  // ── Loading state ────────────────────────────────────────────────────────────
  if (loading && !stats) {
    return (
      <div style={{ padding: "24px 28px", background: PBI.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: "40px", height: "40px", border: `3px solid ${PBI.greyLight}`, borderTop: `3px solid ${PBI.accent}`, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto" }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ marginTop: "14px", color: PBI.textSecondary, fontSize: "14px" }}>Loading SMS statistics…</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px 28px", background: PBI.bg, minHeight: "100vh" }}>

      {/* ── Page header ────────────────────────────────────────────────────── */}
      <div style={{
        background: PBI.headerBg, borderRadius: "4px",
        padding: "20px 28px", marginBottom: "20px",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div style={{ width: "40px", height: "40px", background: PBI.blue, borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className="fas fa-sms" style={{ color: "#fff", fontSize: "16px" }} />
          </div>
          <div>
            <div style={{ color: PBI.headerText, fontSize: "18px", fontWeight: "700", lineHeight: 1.2 }}>
              SMS Delivery Statistics
            </div>
            <div style={{ color: PBI.textMuted, fontSize: "12px", marginTop: "3px" }}>
              Monitor OTP delivery performance · auto-refreshes every 30s
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          {health && (
            <div style={{
              display: "flex", alignItems: "center", gap: "6px",
              background: health.healthy ? "#1a3d2b" : "#3d1a1a",
              border: `1px solid ${health.healthy ? "#2d6a4f" : "#6a2d2d"}`,
              borderRadius: "3px", padding: "5px 12px",
            }}>
              <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: health.healthy ? PBI.green : PBI.red }} />
              <span style={{ color: health.healthy ? "#6ee7b7" : "#fca5a5", fontSize: "12px", fontWeight: "600" }}>
                {health.healthy ? "Healthy" : "Unhealthy"} · {health.deliveryRate}% delivery
              </span>
            </div>
          )}
          <button
            onClick={handleRefresh}
            disabled={loading}
            style={{
              background: "transparent", border: `1px solid #605e5c`,
              color: PBI.textMuted, borderRadius: "3px", padding: "6px 14px",
              fontSize: "12px", cursor: loading ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: "6px", opacity: loading ? 0.6 : 1,
            }}
          >
            <i className="fas fa-sync-alt" style={{ fontSize: "11px" }} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Date filter ────────────────────────────────────────────────────── */}
      <div style={{ background: PBI.surface, border: `1px solid ${PBI.border}`, borderRadius: "4px", padding: "16px 20px", marginBottom: "20px" }}>
        <form onSubmit={handleDateFilter} style={{ display: "flex", alignItems: "flex-end", gap: "12px", flexWrap: "wrap" }}>
          <div>
            <label style={{ display: "block", fontSize: "11px", fontWeight: "600", color: PBI.textSecondary, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "5px" }}>
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              style={{ padding: "7px 10px", border: `1px solid ${PBI.border}`, borderRadius: "3px", fontSize: "13px", color: PBI.textPrimary, background: PBI.bg, outline: "none" }}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "11px", fontWeight: "600", color: PBI.textSecondary, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "5px" }}>
              End Date
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              style={{ padding: "7px 10px", border: `1px solid ${PBI.border}`, borderRadius: "3px", fontSize: "13px", color: PBI.textPrimary, background: PBI.bg, outline: "none" }}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            style={{ padding: "8px 18px", borderRadius: "3px", border: "none", background: PBI.blue, color: "#fff", fontSize: "12px", fontWeight: "700", cursor: "pointer" }}
          >
            Apply Filter
          </button>
          {(dateRange.startDate || dateRange.endDate) && (
            <button
              type="button"
              onClick={handleClearFilter}
              style={{ padding: "8px 14px", borderRadius: "3px", border: `1px solid ${PBI.border}`, background: PBI.surface, color: PBI.textSecondary, fontSize: "12px", cursor: "pointer" }}
            >
              Clear
            </button>
          )}
        </form>
      </div>

      {/* ── No data state ──────────────────────────────────────────────────── */}
      {!stats && !loading && (
        <div style={{ background: PBI.surface, border: `1px solid ${PBI.border}`, borderRadius: "4px", padding: "64px 20px", textAlign: "center" }}>
          <i className="fas fa-comment-slash" style={{ fontSize: "32px", color: PBI.textMuted, display: "block", marginBottom: "12px" }} />
          <div style={{ fontSize: "16px", fontWeight: "700", color: PBI.textPrimary, marginBottom: "6px" }}>No SMS Data Available</div>
          <div style={{ fontSize: "13px", color: PBI.textSecondary }}>Start sending OTPs to see statistics here</div>
        </div>
      )}

      {stats && (
        <>
          {/* ── KPI row ──────────────────────────────────────────────────────── */}
          <div style={{ display: "flex", gap: "14px", marginBottom: "20px", flexWrap: "wrap" }}>
            <KpiCard label="Total Sent"    value={stats.summary.totalSent}                              color={PBI.blue}   softColor={PBI.blueSoft}   icon="fas fa-paper-plane" />
            <KpiCard label="Delivered"     value={stats.summary.delivered}                              color={PBI.green}  softColor={PBI.greenSoft}  icon="fas fa-check-circle" />
            <KpiCard label="Failed"        value={stats.summary.failed + stats.summary.undelivered}     color={PBI.red}    softColor={PBI.redSoft}    icon="fas fa-times-circle" />
            <KpiCard label="Delivery Rate" value={stats.summary.deliveryRate}                           color={PBI.purple} softColor={PBI.purpleSoft} icon="fas fa-chart-pie"    sub="of all sent messages" />
            <KpiCard label="Pending"       value={stats.summary.pending}                                color={PBI.amber}  softColor={PBI.amberSoft}  icon="fas fa-clock" />
          </div>

          {/* ── Charts row ───────────────────────────────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "20px" }}>

            {/* Pie chart */}
            <SectionCard
              title="Delivery Status Distribution"
              subtitle={`Period: ${stats.period?.startDate || "—"} → ${stats.period?.endDate || "—"}`}
            >
              {pieChartData && (
                <div style={{ maxWidth: "340px", margin: "0 auto" }}>
                  <Pie
                    data={pieChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: { position: "bottom", labels: { font: { size: 12 }, padding: 16 } },
                      },
                    }}
                  />
                </div>
              )}
            </SectionCard>

            {/* Platform breakdown */}
            <SectionCard title="Platform Breakdown">
              {stats.byPlatform && stats.byPlatform.length > 0 ? (
                <div style={{ overflowX: "auto" }}>
                  <Table style={{ marginBottom: 0, width: "100%" }}>
                    <thead>
                      <tr>
                        {["Platform", "Total", "Delivered", "Rate"].map((h, i) => (
                          <th key={h} style={{ ...thStyle, borderRight: i === 3 ? "none" : undefined }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {stats.byPlatform.map((platform, index) => {
                        const rate = platform.total > 0 ? ((platform.delivered / platform.total) * 100).toFixed(1) : 0;
                        return (
                          <tr key={index}
                            style={{ background: index % 2 === 0 ? PBI.surface : "#faf9f8" }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = PBI.greyLight; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = index % 2 === 0 ? PBI.surface : "#faf9f8"; }}
                          >
                            <td style={tdStyle}>
                              <span style={{ background: PBI.blueSoft, color: PBI.blue, border: `1px solid #b3d9ff`, borderRadius: "3px", padding: "2px 8px", fontSize: "11px", fontWeight: "700" }}>
                                {platform._id || "Unknown"}
                              </span>
                            </td>
                            <td style={{ ...tdStyle, fontWeight: "700" }}>{platform.total}</td>
                            <td style={{ ...tdStyle, color: PBI.green, fontWeight: "700" }}>{platform.delivered}</td>
                            <td style={tdStyle}>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <ProgressBar value={Number(rate)} color={Number(rate) >= 90 ? PBI.green : Number(rate) >= 70 ? PBI.amber : PBI.red} />
                                <span style={{ fontSize: "12px", fontWeight: "700", color: PBI.textPrimary, minWidth: "36px" }}>{rate}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "40px 0", color: PBI.textMuted, fontSize: "13px" }}>
                  <i className="fas fa-mobile-alt" style={{ fontSize: "24px", display: "block", marginBottom: "8px" }} />
                  No platform data available
                </div>
              )}
            </SectionCard>
          </div>

          {/* ── Status + Failures row ────────────────────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>

            {/* Status breakdown */}
            <SectionCard title="Status Breakdown">
              <div style={{ overflowX: "auto" }}>
                <Table style={{ marginBottom: 0, width: "100%" }}>
                  <thead>
                    <tr>
                      {["Status", "Count", "Share"].map((h, i) => (
                        <th key={h} style={{ ...thStyle, borderRight: i === 2 ? "none" : undefined }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(stats.byStatus || []).map((status, index) => {
                      const pct = stats.summary.totalSent > 0
                        ? ((status.count / stats.summary.totalSent) * 100).toFixed(1)
                        : 0;
                      return (
                        <tr key={index}
                          style={{ background: index % 2 === 0 ? PBI.surface : "#faf9f8" }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = PBI.greyLight; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = index % 2 === 0 ? PBI.surface : "#faf9f8"; }}
                        >
                          <td style={tdStyle}><StatusChip status={status._id} /></td>
                          <td style={{ ...tdStyle, fontWeight: "700" }}>{status.count}</td>
                          <td style={tdStyle}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <ProgressBar
                                value={Number(pct)}
                                color={status._id === "delivered" ? PBI.green : status._id === "failed" ? PBI.red : PBI.amber}
                              />
                              <span style={{ fontSize: "12px", fontWeight: "700", minWidth: "36px" }}>{pct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </SectionCard>

            {/* Recent failures */}
            <SectionCard title="Recent Failures" subtitle="Last 10 failed deliveries">
              {stats.recentFailures && stats.recentFailures.length > 0 ? (
                <div style={{ overflowX: "auto" }}>
                  <Table style={{ marginBottom: 0, width: "100%" }}>
                    <thead>
                      <tr>
                        {["Phone", "Error Code", "Message", "Time"].map((h, i) => (
                          <th key={h} style={{ ...thStyle, borderRight: i === 3 ? "none" : undefined }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recentFailures.map((failure, index) => (
                        <tr key={index}
                          style={{ background: index % 2 === 0 ? PBI.surface : "#faf9f8" }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = PBI.greyLight; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = index % 2 === 0 ? PBI.surface : "#faf9f8"; }}
                        >
                          <td style={{ ...tdStyle, fontWeight: "600", fontFamily: "monospace", fontSize: "12px" }}>
                            {failure.phoneNumber}
                          </td>
                          <td style={tdStyle}>
                            <ErrorChip code={failure.errorCode} />
                          </td>
                          <td style={{ ...tdStyle, fontSize: "12px", color: PBI.textSecondary, maxWidth: "200px" }}>
                            <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {failure.errorMessage || "—"}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, fontSize: "11px", color: PBI.textMuted, whiteSpace: "nowrap" }}>
                            {new Date(failure.createdAt).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: PBI.greenSoft, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
                    <i className="fas fa-check" style={{ fontSize: "20px", color: PBI.green }} />
                  </div>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: PBI.textPrimary }}>No recent failures</div>
                  <div style={{ fontSize: "12px", color: PBI.textMuted, marginTop: "4px" }}>All recent deliveries succeeded</div>
                </div>
              )}
            </SectionCard>
          </div>
        </>
      )}
    </div>
  );
};

export default SmsStatistics;
